import Cookies, { CookieGetOptions, CookieSetOptions } from 'universal-cookie';
import Configuration from '../dispatcher/config/Configuration';
import { i18n } from '../index';
import { COOKIE } from '../dispatcher/config/Constants';
import _ from 'lodash';
import LocalCache from './LocalCache';
import { matchPath } from 'react-router-dom';
import { 
  AuthRoutesConfig 
} from '../dispatcher/authentication/services/AuthSettings';

type PersistDefaultCookie = {
    name?: string; // name of cookie
    persistConfiguration?: boolean; // Persists default cookies within cookie lists
}

type GroupedCookies = {
    predefined: string[],
    other: string[]
};

const cookie = new Cookies();
const { COOKIES, CONSENT } = Configuration.get();
const duration: number = _.get(COOKIES, 'max.user.cookie.duration.period', 60); // default cookie duration 60 days
const {
  url = null,
  analytics = {
    name: 'consent-analytics',
    value: 'yes' // true value
  },
  functional = {
    name: 'consent-functional',
    value: 'yes'
  },
  required = {
    name: 'consent-required'
  },
  sameSite = 'none'
} = CONSENT;

class CookieUtilAbstract {
    private static _sameSite: boolean | 'none' | 'lax' | 'strict' = 'none';
    private static _instance: any;
    /**
     * Use '/' as the path if you want your cookie to be accessible on all pages
     * rootPath {string}
     */
    private static rootPath: string = '/';
    private consentUrl: string | null = url;
    private defaultCookieDuration: number = duration;
    /**
     * List of all analytics cookies.
     */
    private analyticsCookies: PersistDefaultCookie[] = [];
    /**
     * List of all functional cookies.
     */
    private functionalCookies: PersistDefaultCookie[] = [{
      name: COOKIE.LANGUAGE_LOCALE,
      persistConfiguration: true
    }, {
      name: COOKIE.THEME,
      persistConfiguration: true
    }];
    /**
     * List of all required cookies.
     */
    private requiredCookies: PersistDefaultCookie[] = [{
      persistConfiguration: true
    }];

    constructor() {
      this.hasAnalyticsCookie();
      this.hasFunctionalCookie();
      this.hasRequiredCookie();
    }

    public static get Instance() {
      return this._instance || (this._instance = new this());
    }

    /**
     * Refresh cookie duration if cookie exists.
     * @param name
     */
    private refreshCookieDuration = (name: string) => {
      let value: any = this.get(name);
      if (value) {
        this.set(name, value, {
          expires: this.getDuration()
        });
      }
    };
    /**
     * Setter for consent url.
     * After activating consent feature, refresh predefined functional cookies duration time.
     * @param url
     */
    setConsentUrl = (url: string) => {
      this.consentUrl = url;
      this.functionalCookies?.map((cookie: PersistDefaultCookie) => {
        if (cookie.persistConfiguration) {
          this.refreshCookieDuration(cookie.name);
        }
      });
    };
    /**
     * Getter for consent url.
     */
    getConsentUrl = (): string => this.consentUrl;
    /**
     * Setter for default duration.
     * @param defaultDuration
     */
    setDefaultDuration = (defaultDuration: number) => {
      this.defaultCookieDuration = defaultDuration;
    };
    /**
     * Getter for default duration.
     */
    getDefaultDuration = (): number => this.defaultCookieDuration;
    /**
     * Get cookie.
     * @param name
     * @param options
     */
    get = (name: string, options?: CookieGetOptions) => {
      let setViaConsentPage: boolean = [analytics.name, functional.name, required.name].includes(name);
      let isRequired: boolean = this.requiredCookies.map((cookie: PersistDefaultCookie) => cookie.name).includes(name);
      let isFunctional: boolean = this.functionalCookies.map((cookie: PersistDefaultCookie) => cookie.name).includes(name);
      let isAnalytics: boolean = this.analyticsCookies.map((cookie: PersistDefaultCookie) => cookie.name).includes(name);
      let setViaCookieUtil: boolean = !!name && (isRequired || isFunctional || isAnalytics || setViaConsentPage);
      if (!setViaCookieUtil && ![COOKIE.THEME, COOKIE.LANGUAGE_LOCALE].includes(name)) {
        // <debug>
        console.warn(`Cookie with name ${name} is not set trough dispatcher. Please use this cookie with caution!`);
        // </debug>
      }
      return cookie.get(name, options);
    };
    /**
     * Remove cookie.
     * @param name
     * @param options
     */
    remove = (name: string, options: CookieSetOptions = {}) => {
      this.removeCookie(this.analyticsCookies, name);
      this.removeCookie(this.functionalCookies, name);
      this.removeCookie(this.requiredCookies, name);
      if (!options.path) {
        options.path = CookieUtilAbstract.rootPath;
      }
      cookie.remove(name, options);
    };
    /**
     * Set cookie.
     * @param name
     * @param value
     * @param options
     */
    set = (name: string, value: any, options: CookieSetOptions = {
      expires: this.getDuration()
    }) => {
      if (!options.path) {
        options.path = CookieUtilAbstract.rootPath;
      }
      if (!options.sameSite) {
        options.sameSite = CookieUtilAbstract._sameSite;
      }
      if (options.sameSite) {
        options.secure = true;
      }

      cookie.set(name, value, options);
    };
    /**
     * Set analytical cookie.
     * @param name
     * @param value
     * @param options
     */
    setAnalyticsCookie = (name: string, value: any, options?: CookieSetOptions) => {
      if (this.hasAnalyticsCookie()) {
        const { predefined, other } = this.getCookieGroup(this.analyticsCookies);
        if (!predefined.includes(name) && !other.includes(name)) {
          this.analyticsCookies.push({
            name,
            persistConfiguration: false
          });
        }
        this.set(name, value, options);
      } else {
        this.clearAllAnalyticalCookies();
      }
    };
    /**
     * Set functional cookie.
     * @param name
     * @param value
     * @param options
     */
    setFunctionalCookie = (name: string, value: any, options?: CookieSetOptions) => {
      if (this.hasFunctionalCookie()) {
        const { predefined, other } = this.getCookieGroup(this.functionalCookies);
        if (!predefined.includes(name) && !other.includes(name)) {
          this.functionalCookies.push({
            name,
            persistConfiguration: false
          });
        }
        this.set(name, value, options);
      } else {
        this.clearAllFunctionalCookies();
      }
    };
    /**
     * Set required cookie.
     * @param name
     * @param value
     * @param options
     */
    setRequiredCookie = (name: string, value: any, options?: CookieSetOptions) => {
      if (this.hasRequiredCookie()) {
        const { predefined, other } = this.getCookieGroup(this.requiredCookies);
        if (!predefined.includes(name) && !other.includes(name)) {
          this.requiredCookies.push({
            name,
            persistConfiguration: false
          });
        }
        this.set(name, value, options);
      } else {
        this.remove(name);
      }
    };
    /**
     * Clears all analytical cookies.
     */
    clearAllAnalyticalCookies = () => {
      for (let i = 0; i < this.analyticsCookies.length; i++) {
        this.remove(this.analyticsCookies[i].name);
      }
    };
    /**
     * Clears all functional cookies.
     */
    clearAllFunctionalCookies = () => {
      for (let i = 0; i < this.functionalCookies.length; i++) {
        this.remove(this.functionalCookies[i].name);
      }
    };
    /**
     * Clears all required cookies.
     */
    clearAllRequiredCookies = () => {
      for (let i = 0; i < this.requiredCookies.length; i++) {
        this.remove(this.requiredCookies[i].name);
      }
    };
    /**
     * Changes language locales if functional cookies are accepted.
     * Returns boolean which indicates status.
     * @param nextLocale
     */
    changeLanguage = (nextLocale: string): boolean => {
      let success: boolean = this.hasFunctionalCookie();
      if (success) {
        i18n.changeLanguage(nextLocale).finally(() => {
          this.setLocale(nextLocale);
        });
      }
      return success;
    };
    /**
     * Getter for analytics cookie consent.
     */
    getAnalyticsConsent = () => this.get(analytics.name);
    /**
     * Getter for functional cookie consent.
     */
    getFunctionalConsent = () => this.get(functional.name);
    /**
     * Getter for required cookie consent.
     */
    getRequiredConsent = () => this.get(required.name);
    /**
     * Handler for before route changes.
     */
    beforeRouteChange = () => {
      const path = Configuration.getPathname();
      if (!AuthRoutesConfig.filter((item: any) => matchPath(path, item)).length) {
        LocalCache.setItem('route', path);
      }
    };
    /**
     * Getter for cookie duration.
     * @param days (defaults to duration set trough properties.config file)
     */
    getDuration = (days: number = this.getDefaultDuration()): Date => new Date(Date.now() + (days * 24 * 60 * 60 * 1000));
    /**
     * Checks if analytics cookies are enabled. Returns `true` if analytics cookies are enabled.
     * If analytics cookies are not enabled, all analytics cookies will be cleared.
     */
    hasAnalyticsCookie = (): boolean => {
      let hasAnalyticsCookie: boolean = !this.hasConsentUrl() || analytics.value === this.getAnalyticsConsent();
      if (!hasAnalyticsCookie) {
        this.clearAllAnalyticalCookies();
      }
      return hasAnalyticsCookie;
    };
    /**
     * Checks if consent url is provided.
     */
    hasConsentUrl = (): boolean => !!this.getConsentUrl();
    /**
     * Checks if functional cookies are enabled. Returns `true` if functional cookies are enabled.
     * If functional cookies are not enabled, all functional cookies will be cleared.
     */
    hasFunctionalCookie = (): boolean => {
      let hasFunctionalityCookie: boolean = !this.hasConsentUrl() || functional.value === this.getFunctionalConsent();
      if (!hasFunctionalityCookie) {
        this.clearAllFunctionalCookies();
      }
      return hasFunctionalityCookie;
    };
    /**
     * Checks if required cookies are enabled. Returns `true` if required cookies are enabled.
     */
    hasRequiredCookie = (): boolean => !this.hasConsentUrl() || !!this.getRequiredConsent();
    /**
     * Getter for locale cookie.
     */
    getLocale = (): string => this.get(COOKIE.LANGUAGE_LOCALE);
    /**
     * Setter for locale cookie. If functional cookies are not enabled. Locale cookie will be cleared.
     * @param locale
     */
    private setLocale = (locale: string) => {
      this.setPredefinedCookie(COOKIE.LANGUAGE_LOCALE, locale, {
        expires: this.getDuration()
      });
    };
    /**
     * Getter for theme cookie.
     */
    getTheme = (): string => this.get(COOKIE.THEME);
    /**
     * Setter for theme cookie. If functional cookies are not enabled. Theme cookie will be cleared.
     * @param theme
     */
    private setTheme = (theme: string) => {
      this.setPredefinedCookie(COOKIE.THEME, theme, {
        expires: this.getDuration()
      });
    };

    /**
     * Set cookies which are by default in one of lists (requiredCookies, functionalCookies, analyticsCookies)
     * @param name
     * @param value
     * @param options
     */
    setPredefinedCookie = (name: string, value: any, options: any) => {
      if (this.getCookieGroup(this.requiredCookies).predefined.includes(name)) {
        this.setRequiredCookie(name, value, options);
      } else if (this.getCookieGroup(this.functionalCookies).predefined.includes(name)) {
        this.setFunctionalCookie(name, value, options);
      } else if (this.getCookieGroup(this.analyticsCookies).predefined.includes(name)) {
        this.setAnalyticsCookie(name, value, options);
      }
    };
    private getCookieGroup = (cookieList: PersistDefaultCookie[]): GroupedCookies => {
      let group: GroupedCookies = {
        predefined: [],
        other: []
      };
      cookieList.reduce((group: GroupedCookies, cookie: PersistDefaultCookie) => {
        if (!cookie.persistConfiguration) {
          group.other.push(cookie.name);
        } else {
          group.predefined.push(cookie.name);
        }
        return group;
      }, group);
      return group;
    };

    /**
     * Remove cookie from internal list.
     * @param cookieList
     * @param name
     */
    private removeCookie = (cookieList: PersistDefaultCookie[], name: string) => {
      const { other } = this.getCookieGroup(cookieList);
      let index: number = other.indexOf(name);
      if (index !== -1) {
        index = _.findIndex(cookieList, {
          name,
          persistConfiguration: false
        });
        cookieList.splice(index, 1);
      }
    };
}
const CookieUtil = CookieUtilAbstract.Instance;
// <debug>
// @ts-ignore
window.CookieUtil = CookieUtil;
// </debug>
export default CookieUtil;
